import { MerchantOffer } from '../graphql/fragments/offers';
import { RestApiUrl } from '../config';
import { logEventWithoutAuthentication } from './events';
import { logAmplitudeEventWithoutAuthentication } from '../../lib/events/amplitudeEvents';
import { getDevStackMode } from '../../lib/common/devStackMode';

export type PartialMerchantOffer = Pick<
    MerchantOffer,
    | 'offerId'
    | 'merchantId'
    | 'card'
    | 'points'
    | 'fixedPoints'
    | 'nonBoostedPoints'
    | 'nonBoostedFixedPoints'
    | 'multipleRatesForDisplay'
    | 'conditions'
> & { __typename: 'PartialMerchantOffer' };

export type OnlineOfferActivationInfo = {
    partialOffer: PartialMerchantOffer;
    personalizedAffiliateLink: string | undefined;
    hasRedirectUrl: boolean;
    addRedirectUrlFailed: boolean;
    parametersRemovedFromRedirectUrl: string[];
    hasAcknowledgedWarningForCookieTracking: boolean;
};

export type FetchOnlineOfferActivationInfoResult =
    | { status: 'success'; data: OnlineOfferActivationInfo }
    | { status: 'webOnlyAccountBlocked' }
    | { status: 'newAccountBlocked' }
    | { status: 'userBanned' }
    | { status: 'error' };

/** Body of the 403 returned when the account never used the mobile app or an extension and the guard is on for its region */
const WEB_ONLY_ACCOUNT_AFFILIATE_LINKS_DISABLED_ERROR_BODY = 'WebOnlyAccountAffiliateLinksDisabled';
/** Body of the 403 returned when the account is too recent to get a link from the web and the guard is on for its region */
const NEW_ACCOUNT_AFFILIATE_LINKS_DISABLED_ERROR_BODY = 'NewAccountAffiliateLinksDisabled';

export async function fetchOnlineOfferActivationInfo(
    userId: string,
    offerId: string,
    shouldUseDevStack?: boolean,
    redirectUrl?: string,
    triggeringEventPlatform?: string
): Promise<FetchOnlineOfferActivationInfoResult> {
    console.log(`API call: getting the activation information for offer ${offerId}...`);
    const headers: Headers = new Headers();
    headers.append('Accept', 'application/json');
    headers.append('Content-Type', 'application/json');
    const isDevStackMode = shouldUseDevStack ?? getDevStackMode();
    const restApiUrl = isDevStackMode ? RestApiUrl.dev : RestApiUrl.prod;
    const url =
        `${restApiUrl}user/online-offer-activation-info` +
        `?userId=${encodeURIComponent(userId)}&offerId=${encodeURIComponent(offerId)}` +
        `&isMobileSafariExtension=${triggeringEventPlatform === 'mobileSafariExtension' ? 'true' : 'false'}` +
        `&redirectUrl=${redirectUrl ? encodeURIComponent(redirectUrl) : ''}` +
        (triggeringEventPlatform ? `&triggeringEventPlatform=${triggeringEventPlatform}` : '');
    try {
        const offerActivationResponse = await fetch(url, { method: 'GET', headers: headers });
        console.log(`Response ${offerActivationResponse.status} for API call 'get online offer activation info'`);
        if (offerActivationResponse.status === 200) {
            const offerActivationInfo: OnlineOfferActivationInfo | undefined = await offerActivationResponse.json();
            if (offerActivationInfo) {
                offerActivationInfo.partialOffer.__typename = 'PartialMerchantOffer';
                const { personalizedAffiliateLink, addRedirectUrlFailed } = offerActivationInfo;
                if (personalizedAffiliateLink && addRedirectUrlFailed)
                    logAddRedirectUrlToAffiliateLinkFailed({ userId, offerId, isDevStackMode });
                return { status: 'success', data: offerActivationInfo };
            }
        }
        if (offerActivationResponse.status === 403) {
            const body = await offerActivationResponse.text();
            if (body === WEB_ONLY_ACCOUNT_AFFILIATE_LINKS_DISABLED_ERROR_BODY) return { status: 'webOnlyAccountBlocked' };
            if (body === NEW_ACCOUNT_AFFILIATE_LINKS_DISABLED_ERROR_BODY) return { status: 'newAccountBlocked' };
            return { status: 'userBanned' };
        }
    } catch {}
    console.error('Error: could not fetch online offer activation info');
    return { status: 'error' };
}

async function logAddRedirectUrlToAffiliateLinkFailed({
    userId,
    offerId,
    isDevStackMode,
}: {
    userId: string;
    offerId: string;
    isDevStackMode: boolean;
}) {
    const payload = { offerId };
    await Promise.all([
        logEventWithoutAuthentication(userId, { type: 'addRedirectUrlToAffiliateLinkFailed', payload }, isDevStackMode),
        logAmplitudeEventWithoutAuthentication(
            {
                name: 'Offers - Add Redirect URL To Affiliate Link Failed',
                properties: payload,
            },
            { userId }
        ),
    ]);
}
